/**
 * Client-side video poster-frame thumbnail: grabs a still frame near the
 * start of the clip and re-encodes it as a small JPEG. The server has no
 * video decoder and can't extract one itself, so this must run before
 * upload — mirrors the poster-frame capture already done natively on iOS
 * (AVAssetImageGenerator) and Android (MediaMetadataRetriever).
 */
import { THUMB_MAX_DIM_SUM } from './uploadImage';

const THUMB_QUALITY = 0.6;

/** Returns null (rather than throwing) if a frame can't be grabbed — the
 * caller just uploads without a thumbnail in that case. */
export async function extractVideoPosterFrame(file: File): Promise<Blob | null> {
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.src = url;

    await new Promise<void>((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error('Could not load video.'));
    });

    // Seek a touch past the very first frame — many encoders' frame 0 is
    // black/blank — then wait for the browser to actually land there.
    const seekTo = Math.min(0.1, (video.duration || 0) / 2);
    if (seekTo > 0) {
      await new Promise<void>((resolve) => {
        video.onseeked = () => resolve();
        video.currentTime = seekTo;
      });
    }

    const { width, height } = scaleToSum(video.videoWidth, video.videoHeight, THUMB_MAX_DIM_SUM);
    if (width <= 0 || height <= 0) return null;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, width, height);

    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', THUMB_QUALITY);
    });
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function scaleToSum(w: number, h: number, maxSum: number): { width: number; height: number } {
  if (!w || !h) return { width: 0, height: 0 };
  const sum = w + h;
  if (sum <= maxSum) return { width: w, height: h };
  const factor = maxSum / sum;
  return {
    width: Math.max(1, Math.round(w * factor)),
    height: Math.max(1, Math.round(h * factor)),
  };
}
